import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GUESTGET, httpOk, resolveUploadUrl } from 'helpers/api'

/**
 * Preview приложения в кабинете. В отличие от сайта здесь нечего открыть по домену —
 * канал резолвится по uuid, а значит preview доступен только после первого сохранения.
 * Рисуем ровно то, что реально придёт в сборку: тему, лого, баннеры и меню из /mobile-app/*.
 */
function MobileAppPreview({ uuid }) {
	const { t } = useTranslation()

	const [config, setConfig] = useState(null)
	const [menu, setMenu] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	async function load() {
		if (!uuid) {
			setConfig(null)
			setMenu([])
			setError(t('mobile_app_preview_save_first'))
			return
		}

		setLoading(true)
		setError('')

		const configResponse = await GUESTGET(`/mobile-app/config/${uuid}`)
		if (!httpOk(configResponse)) {
			setLoading(false)
			setConfig(null)
			setMenu([])
			setError(t('mobile_app_preview_error'))
			return
		}

		const settings = configResponse.data?.data ?? configResponse.data
		setConfig(settings)

		const defaultPos = settings?.poses?.find(pos => pos.is_default) ?? settings?.poses?.[0]
		const posId = defaultPos?.id
		if (!posId) {
			setLoading(false)
			setMenu([])
			setError(t('mobile_app_preview_no_pos'))
			return
		}

		const menuResponse = await GUESTGET(`/mobile-app/menu/${uuid}?pos_id=${posId}`)
		if (httpOk(menuResponse)) {
			setMenu(extractMenu(menuResponse.data))
		}

		setLoading(false)
	}

	useEffect(() => {
		load()
	}, [uuid]) // eslint-disable-line react-hooks/exhaustive-deps

	const colors = config?.colors ?? {}
	const primaryColor = colors.primary?.default ?? '#e11d48'
	const primaryTextColor = colors.primary?.text ?? '#ffffff'
	const backgroundColor = colors.background?.default ?? '#ffffff'
	const mutedBackgroundColor = colors.background?.muted ?? '#f1f5f9'
	const cardColor = colors.background?.card ?? '#ffffff'
	const textColor = colors.text?.default ?? '#1f2933'
	const mutedColor = colors.text?.muted ?? '#6b7280'
	const categories = useMemo(() => Array.isArray(menu) ? menu : [], [menu])
	const products = useMemo(() => flattenProducts(categories).slice(0, 6), [categories])
	const brandName = config?.brand_name || config?.name || 'Mison'
	const banner = config?.banners?.[0]

	if (loading) {
		return <div className="card"><div className="card-body text-center py-5">{t('loading')}</div></div>
	}

	if (error) {
		return (
			<div className="card">
				<div className="card-body text-center py-5">
					<h5 className="mb-2">{t('mobile_app_preview_unavailable')}</h5>
					<p className="text-muted mb-0">{error}</p>
				</div>
			</div>
		)
	}

	if (!config) {
		return null
	}

	return (
		<div className="card">
			<div className="card-body d-flex justify-content-center">
				{/* Рамка телефона: 390×844 — размеры, на которые верстается приложение */}
				<div
					style={{
						width: 390,
						height: 844,
						borderRadius: 44,
						padding: 12,
						background: '#111827',
						boxShadow: '0 30px 60px rgba(15, 23, 42, .25)',
					}}>
					<div
						style={{
							height: '100%',
							borderRadius: 34,
							overflow: 'hidden auto',
							// Как в телефоне: листается, но полосы прокрутки не видно
							scrollbarWidth: 'none',
							background: backgroundColor,
							color: textColor,
						}}>
						<div className="px-3 pt-4 pb-3 d-flex align-items-center gap-3" style={{ background: cardColor }}>
							<div className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 14, background: mutedBackgroundColor }}>
								{config.logo
									? <img src={config.logo} alt={brandName} style={{ maxWidth: 34, maxHeight: 34, objectFit: 'contain' }} />
									: <b style={{ color: primaryColor }}>{brandName.slice(0, 1)}</b>
								}
							</div>
							<div className="overflow-hidden">
								<div className="fw-bold text-truncate" style={{ fontSize: 17 }}>{brandName}</div>
								<div className="text-truncate" style={{ color: mutedColor, fontSize: 13 }}>
									{config.poses?.[0]?.name || config.description || t('delivery')}
								</div>
							</div>
						</div>

						<div className="px-3 pb-4">
							<div
								className="mb-3 d-flex align-items-end p-3"
								style={{
									height: 150,
									borderRadius: 22,
									background: banner?.image
										? `linear-gradient(180deg, rgba(0,0,0,.05), rgba(0,0,0,.6)), url(${banner.image}) center / cover no-repeat`
										: `linear-gradient(135deg, ${primaryColor}, ${colors.secondary?.default ?? primaryColor})`,
									// На чистой заливке белый пропадал у светлого акцента (ч/б тёмный — белый)
									color: banner?.image ? '#fff' : primaryTextColor,
								}}>
								<div className="fw-bold" style={{ fontSize: 18 }}>{config.description || brandName}</div>
							</div>

							{!!categories.length &&
								<div className="d-flex gap-2 overflow-auto pb-2 mb-3" style={{ scrollbarWidth: 'none' }}>
									{categories.map((category, index) => (
										<span
											className="px-3 py-2 flex-shrink-0"
											key={category.id ?? category.name ?? index}
											style={{
												borderRadius: 999,
												fontSize: 13,
												background: index === 0 ? primaryColor : cardColor,
												color: index === 0 ? primaryTextColor : textColor,
												boxShadow: '0 6px 16px rgba(15, 23, 42, .08)',
												whiteSpace: 'nowrap',
											}}>
											{category.name}
										</span>
									))}
								</div>
							}

							{products.length
								? <div className="row row-cols-2 g-2">
									{products.map(product => (
										<div className="col" key={`${product.product_id ?? product.id}-${product.name}`}>
											<div className="h-100 p-2 d-flex flex-column" style={{ background: cardColor, borderRadius: 18, boxShadow: '0 8px 20px rgba(15, 23, 42, .08)' }}>
												<div className="mb-2" style={{ height: 96, borderRadius: 14, background: mutedBackgroundColor, overflow: 'hidden' }}>
													{product.image &&
														<img src={resolveUploadUrl(product.image, 'products')} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
													}
												</div>
												<div className="fw-600 mb-1" style={{ fontSize: 14, lineHeight: 1.2 }}>{product.name}</div>
												<div className="mt-auto d-flex justify-content-between align-items-center">
													<span className="fw-bold" style={{ color: primaryColor, fontSize: 14 }}>
														{formatPrice(product.sale_price ?? product.price, t('sum'))}
													</span>
													<span
														className="d-flex align-items-center justify-content-center"
														style={{ width: 28, height: 28, borderRadius: '50%', background: primaryColor, color: primaryTextColor }}>
														+
													</span>
												</div>
											</div>
										</div>
									))}
								</div>
								: <div className="p-4 text-center" style={{ background: cardColor, borderRadius: 18, color: mutedColor }}>{t('menu_empty')}</div>
							}
						</div>

						<div
							className="d-flex justify-content-around align-items-center py-3 sticky-bottom"
							style={{ background: cardColor, borderTop: `1px solid ${mutedBackgroundColor}` }}>
							{['menu', 'cart', 'orders', 'profile'].map((item, index) => (
								<span key={item} style={{ fontSize: 12, color: index === 0 ? primaryColor : mutedColor }}>{t(item)}</span>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function extractMenu(payload) {
	const data = payload?.data ?? payload
	if (Array.isArray(data)) return data
	if (Array.isArray(data?.menu)) return data.menu
	if (Array.isArray(data?.categories)) return data.categories
	return []
}

function flattenProducts(categories = []) {
	return categories.flatMap(category => [
		...(category.products ?? []),
		...flattenProducts(category.children ?? []),
	])
}

function formatPrice(value, currency) {
	return `${Number(value ?? 0).toLocaleString('ru-RU')} ${currency}`
}

export default MobileAppPreview
